import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { Input } from "@/shared/ui";

const DEBOUNCE_MS = 250;

/** Top-bar search: debounced 250 ms, drives /search?q= so results are linkable. */
export function SearchBox() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const onSearchPage = location.pathname === "/search";
  const urlQuery = onSearchPage ? (new URLSearchParams(location.search).get("q") ?? "") : "";
  const [text, setText] = useState(urlQuery);
  const [seenUrlQuery, setSeenUrlQuery] = useState(urlQuery);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // The URL changed elsewhere (back button, leaving the search page): follow it.
  if (urlQuery !== seenUrlQuery) {
    setSeenUrlQuery(urlQuery);
    setText(urlQuery);
  }

  useEffect(() => () => clearTimeout(timer.current), []);

  const onChange = (value: string) => {
    setText(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const q = value.trim();
      if (q === "") {
        if (onSearchPage) void navigate("/", { replace: true });
      } else {
        void navigate(`/search?q=${encodeURIComponent(q)}`, { replace: onSearchPage });
      }
    }, DEBOUNCE_MS);
  };

  return (
    <div className="relative max-w-md flex-1">
      <Search
        aria-hidden
        size={15}
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
      />
      <Input
        type="search"
        aria-label="Search files and folders"
        placeholder={t("nav.searchPlaceholder")}
        value={text}
        maxLength={200}
        onChange={(e) => onChange(e.target.value)}
        className="w-full pl-9 pr-3 bg-surface/80 hover:bg-surface focus:bg-surface shadow-xs"
      />
    </div>
  );
}
